// src/components/CitySelectorModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Input, List, Spin } from "antd";
import { useApp } from "../context/AppContext.tsx";
import { platformApiUrl } from "../config/api";

type City = { id: number; name: string };

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CitySelectorModal({ open, onClose }: Props) {
  const { city: selectedCity, setCity, setPrayers } = useApp();

  const [cities, setCities] = useState<City[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(platformApiUrl("namaz/cities"), { cache: "no-store" });
        if (!res.ok) throw new Error(res.statusText);
        const raw: any[] = await res.json();
        setCities(raw.map((c) => ({ id: Number(c.id), name: c.name })));
      } catch (err) {
        console.error("Ошибка загрузки городов:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? cities.filter((c) => c.name.toLowerCase().includes(q)) : cities;
  }, [query, cities]);

  async function pick(c: City) {
    if (selectedCity?.id === c.id) return;
    setSaving(true);

    try {
      // Сохраняем в контекст
      setCity(c);
      localStorage.setItem("city", JSON.stringify(c));

      const res = await fetch(`${platformApiUrl("namaz/prayer-times")}?city_id=${c.id}`, { cache: "no-store" });
      if (!res.ok) throw new Error(res.statusText);
      const times = await res.json();
      setPrayers(times);
    } catch (err) {
      console.error("Ошибка загрузки prayer_times:", err);
    } finally {
      setSaving(false);
      onClose();
    }
  }

  return (
    <Modal
      open={open}
      centered
      destroyOnClose
      title="Выберите город"
      footer={null}
      onCancel={saving ? undefined : onClose}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <Input
            placeholder="Начните вводить…"
            allowClear
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ marginBottom: 12 }}
          />

          <List
            bordered
            dataSource={filtered}
            locale={{ emptyText: "Нет совпадений" }}
            renderItem={(c) => {
              const sel = c.id === selectedCity?.id;
              return (
                <List.Item
                  onClick={() => pick(c)}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    style={{
                      fontWeight: sel ? 700 : 400,
                      color: sel ? "#16c784" : undefined,
                    }}
                  >
                    {c.name}
                  </span>
                </List.Item>
              );
            }}
          />
        </>
      )}

      {saving && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      )}
    </Modal>
  );
}
